import { onError } from '@apollo/client/link/error';

/**
 * Error codes the API returns when a request was refused for rate reasons.
 * Deliberately NOT `CAPTCHA_REQUIRED`: a challenge is not an error to toast — the
 * submitting form owns it (renders the widget and retries), see
 * `hooks/useCreateSoWithCaptcha.hook.tsx` and `captcha.ts`.
 */
const RATE_LIMIT_CODES = ['RATE_LIMITED', 'IP_BLOCKED'];

type RateLimitHandler = (code: string) => void;

let handler: RateLimitHandler | undefined;

/**
 * Register what should happen when the API refuses a request as rate limited —
 * in practice, showing the global message toast.
 *
 * Registered from the app entry rather than imported here, because the Redux
 * slices import from the apollo barrel; reaching back to the store from inside
 * this module would close an import cycle.
 */
export function setRateLimitHandler(fn: RateLimitHandler): void {
  handler = fn;
}

/**
 * Surfaces rate-limit denials to the user.
 *
 * Without this, a denial is silent: several funnel mutations are fired without a
 * `.catch`, so the customer sees nothing at all and assumes the action worked.
 */
export const rateLimitErrorLink = onError(({ graphQLErrors }) => {
  if (!graphQLErrors?.length) return;
  const denial = graphQLErrors.find(error =>
    RATE_LIMIT_CODES.includes(String(error.extensions?.code)),
  );
  if (denial) {
    handler?.(String(denial.extensions?.code));
  }
});
